import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { CompanyPageAccordion } from './CompanyPageAccordion'
import { CreatableTagSelector } from 'components/CreatableTagSelector'
import { CreatableDistributorSelector } from 'components/CreatableDistributorSelector'
import { CreatableGPOSelector } from 'components/CreatableGPOSelector'
import { CreatableCampaignSelector } from 'components/CreatableCampaignSelector'
import FbButton from 'components/FbUI/FbButton'
import { Tag } from 'models/tags'
import {
  CompanyDistributor,
  CompanyGPO,
  CompanyTypeSlug,
} from 'models/companies'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { TagCountBadge } from 'components/FbUI/TagCountBadge'
import { TextPill } from 'components/FbUI/TextPill'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'
import { PiMegaphone } from 'react-icons/pi'

const attributesSchema = z.object({
  taglist: z.any().optional().nullable(),
  distributors: z.any().optional().nullable(),
  gpos: z.any().optional().nullable(),
  campaigns: z.any().optional().nullable(),
  previous_taglist: z.any().optional().nullable(),
  previous_distributors: z.any().optional().nullable(),
  previous_gpos: z.any().optional().nullable(),
  previous_campaigns: z.any().optional().nullable(),
})

export type CompanyAttributesInput = z.infer<typeof attributesSchema>

interface CompanyAttributesAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: CompanyTypeSlug
}

export function CompanyAttributesAccordion({
  chainProxyId,
  doorProxyId,
  companyType = 'restaurants-bars',
}: CompanyAttributesAccordionProps) {
  const queryClient = useQueryClient()
  const api = apiService()

  // Fetch options for selectors
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()
  const { data: gposOptions } = api.useGetCompanyGPOsOptions()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: campaignsOptions } = api.useGetCampaignsOptions({
    typeOrTypeId: companyType,
    requireFilter: true, // Prevent selecting campaigns when company type is unknown
  })

  // Fetch company or door proxy data based on context
  const { data: effectiveData, refetch: refetchData } = useCompanyOrDoorData(
    chainProxyId,
    doorProxyId
  )

  const [previousDistributors, setPreviousDistributors] = useState<number[]>([])
  const [previousGPOs, setPreviousGPOs] = useState<number[]>([])
  const [previousTaglist, setPreviousTaglist] = useState<number[]>([])
  const [previousCampaigns, setPreviousCampaigns] = useState<number[]>([])

  const { control, handleSubmit, reset, setError, formState } =
    useForm<CompanyAttributesInput>({
      defaultValues: {
        taglist: undefined,
        distributors: undefined,
        gpos: undefined,
        campaigns: undefined,
      },
      resolver: zodResolver(attributesSchema),
    })

  // Initialize form with company or door proxy data
  useEffect(() => {
    if (effectiveData) {
      const taglist: Tag[] = effectiveData.taglist || []
      const distributors: CompanyDistributor[] =
        effectiveData.distributors || []
      const gpos: CompanyGPO[] = effectiveData.gpos || []
      const campaigns: any[] = effectiveData.campaigns || []

      const mappedTaglist = taglist.map((t) => t.id) || []
      const mappedDistributors =
        distributors.map((distributor) => distributor.id) || []
      const mappedGPOs = gpos.map((gpo) => gpo.id) || []
      const mappedCampaigns = campaigns.map((campaign) => campaign.id) || []

      reset({
        taglist: mappedTaglist,
        distributors: mappedDistributors,
        gpos: mappedGPOs,
        campaigns: mappedCampaigns,
      })

      setPreviousDistributors(mappedDistributors)
      setPreviousGPOs(mappedGPOs)
      setPreviousTaglist(mappedTaglist)
      setPreviousCampaigns(mappedCampaigns)
    }
  }, [effectiveData, reset])

  const mutation = useMutation({
    mutationFn: async (values: CompanyAttributesInput) => {
      if (doorProxyId) {
        // Use door attrs API (doesn't support campaigns)
        await api.bulkEditDoorAttrs({
          door_proxy_ids: [doorProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
        })
      } else {
        // Use company attrs API
        await api.bulkEditCompanyAttrs({
          contact_company_ids: [chainProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          campaigns:
            Array.isArray(values.campaigns) && values.campaigns.length > 0
              ? values.campaigns
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
          previous_campaigns: previousCampaigns,
        })
      }
    },
    onSettled: async (data, err) => {
      refetchData()
      queryClient.invalidateQueries({ queryKey: ['notes-table'] })
      // Invalidate event history to show new attribute changes
      queryClient.invalidateQueries({
        queryKey: ['companyActivity', chainProxyId, doorProxyId],
      })
      if (err) {
        toast.error(
          `Error updating ${doorProxyId ? 'door' : 'company'} attributes.`
        )
      } else {
        toast.success(
          `${doorProxyId ? 'Door' : 'Company'} attributes updated successfully.`
        )
        reset(formState.defaultValues)
      }
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    mutation.mutate(values)
  })

  // Collapsed content showing badges
  const defaultGrey = MULTI_VALUE_SELECT_COLORS[0].background
  const defaultGreyText = MULTI_VALUE_SELECT_COLORS[0].text

  const collapsedContent = (
    <div className="flex flex-wrap gap-2">
      {/* Tags Badge */}
      {effectiveData?.taglist && effectiveData.taglist.length > 0 && (
        <TagCountBadge count={effectiveData.taglist.length} />
      )}

      {/* Campaigns Badge - Only show for company pages */}
      {!doorProxyId &&
        effectiveData?.campaigns &&
        effectiveData.campaigns.length > 0 && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md">
            <PiMegaphone size={14} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {effectiveData.campaigns.length}{' '}
              {effectiveData.campaigns.length === 1 ? 'campaign' : 'campaigns'}
            </span>
          </div>
        )}

      {/* Distributor Pills */}
      {effectiveData?.distributors &&
        effectiveData.distributors.map((distributor) => (
          <TextPill
            key={distributor.id}
            textColor={defaultGreyText}
            backgroundColor={defaultGrey}
          >
            {distributor.name}
          </TextPill>
        ))}

      {/* GPO Pills */}
      {effectiveData?.gpos &&
        effectiveData.gpos.map((gpo) => (
          <TextPill
            key={gpo.id}
            textColor={defaultGreyText}
            backgroundColor={defaultGrey}
          >
            {gpo.name}
          </TextPill>
        ))}
    </div>
  )

  return (
    <CompanyPageAccordion
      title="Account Attributes"
      collapsedContent={collapsedContent}
    >
      <div>
        {/* Tags Section */}
        <CreatableTagSelector
          name="taglist"
          control={control}
          label="Tags"
          placeholder="Tags"
          options={tagsOptions}
          setError={setError}
          labelClassName="font-inter font-light text-xs leading-[14.6px] tracking-normal mb-2"
          whiteBackground
        />

        {/* Campaigns Section - Only show for company pages (not supported for doors) */}
        {!doorProxyId && (
          <CreatableCampaignSelector
            name="campaigns"
            control={control}
            label="Campaigns"
            placeholder="Campaign(s)"
            options={campaignsOptions}
            setError={setError}
            labelClassName="font-inter font-light text-xs leading-[14.6px] tracking-normal mb-2"
            whiteBackground
            companyType={companyType}
          />
        )}

        {/* Distributors and GPOs Section */}
        <div className="grid grid-cols-2 gap-6">
          {/* Distributors Section */}
          <CreatableDistributorSelector
            name="distributors"
            control={control}
            label="Distributors"
            placeholder="Distributor(s)"
            options={distributorsOptions}
            setError={setError}
            labelClassName="font-inter font-light text-xs leading-[14.6px] tracking-normal mb-2"
            whiteBackground
          />

          {/* GPOs Section */}
          <CreatableGPOSelector
            name="gpos"
            control={control}
            label="Group Purchasing Organization (GPO)"
            placeholder="GPO(s)"
            options={gposOptions}
            setError={setError}
            labelClassName="font-inter font-light text-xs leading-[14.6px] tracking-normal mb-2"
            whiteBackground
          />
        </div>

        {/* Save Button - Only show when form is dirty */}
        {formState.isDirty && (
          <div className="flex justify-end pt-4">
            <FbButton
              loading={mutation.isPending}
              onClick={onSubmit}
              className="bg-brand-hover hover:bg-brand-dark text-white"
            >
              Save
            </FbButton>
          </div>
        )}
      </div>
    </CompanyPageAccordion>
  )
}
